import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { googleAuth } from "@/lib/api";
import {
  Heart,
  Lock,
  MessageCircle,
  Shield,
  MapPin,
  Users,
  Sparkles,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function Landing() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showReferralMessage, setShowReferralMessage] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const oauthClientRef = useRef(null);

  // Capture referral code from URL and store it, show message
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refFromUrl = urlParams.get("ref");
    if (refFromUrl) {
      localStorage.setItem("stokvel_ref", refFromUrl);
      setShowReferralMessage(true);
      setTimeout(() => setShowReferralMessage(false), 5000);
    }
  }, []);

  const refCode = localStorage.getItem("stokvel_ref");

  // Route the user after successful auth based on legal + onboarding state.
  const routeAfterAuth = useCallback((u) => {
    if (!u) {
      navigate("/", { replace: true });
      return;
    }
    if (u.legal_acceptance?.required) {
      navigate("/accept-legal", { replace: true });
    } else if (u.onboarding_complete === false) {
      navigate("/profile/setup", { replace: true });
    } else {
      navigate("/find", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!loading && user) {
      routeAfterAuth(user);
    }
    // Shared global flag — same as Login.jsx
    if (!window.__googleInitialized) {
      loadGoogleScript();
    } else {
      setGoogleAvailable(true);
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!oauthClientRef.current && window.google?.accounts?.oauth2 && clientId) {
        oauthClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "openid email profile",
          callback: handleOAuthCallback,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, navigate]);

  const loadGoogleScript = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn("Google Client ID not configured");
      return;
    }
    if (window.google?.accounts?.id) {
      initGoogle(clientId);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => initGoogle(clientId);
    script.onerror = () => {
      toast.error("Login unavailable", {
        description: "Please check your internet connection and try again.",
      });
    };
    document.head.appendChild(script);
  };

  const initGoogle = (clientId) => {
    if (window.__googleInitialized) return;
    window.__googleInitialized = true;

    window.google.accounts.id.initialize({
      client_id: clientId,
      ux_mode: "popup",
      use_fedcm_for_prompt: false,
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: async (response) => {
        await handleCredentialResponse(response);
      },
    });

    if (window.google?.accounts?.oauth2) {
      oauthClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: "openid email profile",
        callback: handleOAuthCallback,
      });
    }

    setGoogleAvailable(true);
  };

  const handleCredentialResponse = async (response) => {
    try {
      if (!response.credential) {
        throw new Error("No credential received");
      }
      const payload = JSON.parse(atob(response.credential.split(".")[1]));
      await googleAuth(
        response.credential,
        payload.email,
        payload.name,
        payload.picture,
        refCode
      );
      const refreshed = await refresh();
      toast.success("Welcome to Haven!", {
        description: "You've successfully signed in.",
      });
      routeAfterAuth(refreshed);
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Login Failed", {
        description: error.message || "Unable to sign in. Please try again.",
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleOAuthCallback = useCallback(
    async (tokenResponse) => {
      if (tokenResponse.error) {
        console.error("OAuth fallback error:", tokenResponse);
        toast.error("Login Failed", {
          description: "Unable to sign in. Please try again.",
        });
        setIsLoggingIn(false);
        return;
      }
      try {
        const res = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch user info");
        const profile = await res.json();
        await googleAuth(
          tokenResponse.access_token,
          profile.email,
          profile.name,
          profile.picture,
          refCode
        );
        const refreshed = await refresh();
        toast.success("Welcome to Haven!", {
          description: "You've successfully signed in.",
        });
        routeAfterAuth(refreshed);
      } catch (error) {
        console.error("OAuth fallback login failed:", error);
        toast.error("Login Failed", {
          description: error.message || "Unable to sign in. Please try again.",
        });
      } finally {
        setIsLoggingIn(false);
      }
    },
    [refresh, refCode, routeAfterAuth]
  );

  const handleOAuthFallback = useCallback(() => {
    if (oauthClientRef.current) {
      oauthClientRef.current.requestAccessToken();
    } else {
      toast.error("Login unavailable", {
        description: "Please reload the page and try again.",
      });
      setIsLoggingIn(false);
    }
  }, []);

  const handleGoogleLogin = useCallback(() => {
    if (isLoggingIn || !googleAvailable) return;
    setIsLoggingIn(true);
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        handleOAuthFallback();
      } else if (notification.isSkippedMoment()) {
        handleOAuthFallback();
      } else if (notification.isDismissedMoment()) {
        setIsLoggingIn(false);
      }
    });
  }, [isLoggingIn, googleAvailable, handleOAuthFallback]);

  const stats = [
    { label: "Active Users", value: "990+", icon: Users },
    { label: "Matches Made", value: "400+", icon: Heart },
    { label: "Safe Communities", value: "3+", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-6 md:px-12 py-5 bg-transparent">
        <Link to="/" className="flex items-center gap-2 text-white hover:opacity-90 transition-opacity">
          <span className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" fill="currentColor" />
          </span>
          <span className="font-display text-2xl font-bold tracking-tight text-white">Haven</span>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <button
              onClick={() => routeAfterAuth(user)}
              className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Open app
            </button>
          ) : (
            <button
              onClick={handleGoogleLogin}
              disabled={isLoggingIn || !googleAvailable}
              className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-60"
            >
              {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
              {isLoggingIn ? "Signing in..." : "Join with Google"}
            </button>
          )}
        </div>
      </nav>

      {/* Referral Message */}
      {showReferralMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-green-500 text-white px-4 py-2 rounded-full text-sm shadow-lg animate-fade-in">
          <CheckCircle className="w-4 h-4 inline mr-2" />
          You were referred! Sign up to earn 50 tokens & 1 diamond 💎
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-600 via-fuchsia-500 to-orange-400 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <span className="inline-block text-white/90 uppercase tracking-[0.2em] text-xs md:text-sm mb-6 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full">
            Welcome to a safe space
          </span>
          <h1 className="font-display text-white text-4xl md:text-6xl lg:text-7xl leading-[1.1] mb-6">
            Love begins where <span className="italic not-italic">shame</span> ends.
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10">
            Connect with understanding people in a private, judgment‑free community built for your journey.
          </p>
          <button
            onClick={handleGoogleLogin}
            disabled={isLoggingIn || !googleAvailable}
            className="bg-white text-gray-900 px-10 h-14 rounded-full text-lg font-semibold shadow-2xl hover:scale-105 transition-transform disabled:scale-100 disabled:opacity-60 flex items-center justify-center gap-2 mx-auto"
          >
            {isLoggingIn ? <Loader2 className="w-6 h-6 animate-spin" /> : null}
            {isLoggingIn ? "Signing in..." : "Continue with Google"}
          </button>
          <p className="mt-6 text-white/60 text-sm">
            No credit card required · Free to join · Adults 18+
          </p>
          <p className="mt-3 text-white/50 text-xs">
            By continuing you agree to our{" "}
            <Link to="/terms" className="underline hover:text-white">Terms of Use</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>.
          </p>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white/50 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {stats.map((stat, index) => (
            <div key={index} className="space-y-2">
              <stat.icon className="w-10 h-10 mx-auto text-violet-600" />
              <p className="text-4xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-gray-500 text-lg">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-3xl md:text-4xl text-gray-900 text-center mb-12">
            A safer way to connect
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Shield className="w-6 h-6" />}
              title="Verified Profiles"
              body="Every profile is manually reviewed to keep our community authentic and safe."
            />
            <FeatureCard
              icon={<Heart className="w-6 h-6" />}
              title="Meaningful Matches"
              body="Find people who share your health journey and relationship goals."
            />
            <FeatureCard
              icon={<MessageCircle className="w-6 h-6" />}
              title="Private Messaging"
              body="Chat securely with end-to-end encryption. Your conversations stay private."
            />
            <FeatureCard
              icon={<Lock className="w-6 h-6" />}
              title="Privacy First"
              body="Control who sees your profile. Your health status is visible only if you choose."
            />
            <FeatureCard
              icon={<MapPin className="w-6 h-6" />}
              title="Location‑Based Discovery"
              body="Meet people near you or around the world. GPS‑verified locations."
            />
            <FeatureCard
              icon={<Users className="w-6 h-6" />}
              title="Supportive Community"
              body="Join groups, share stories, and connect with a community that understands."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-violet-600 to-fuchsia-500">
        <div className="max-w-3xl mx-auto text-center px-6">
          <Sparkles className="w-12 h-12 mx-auto text-white mb-6" />
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
            Ready to find your community?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join thousands of people who have already found meaningful connections.
          </p>
          <button
            onClick={handleGoogleLogin}
            disabled={isLoggingIn || !googleAvailable}
            className="bg-white text-violet-600 px-10 h-14 rounded-full text-lg font-semibold shadow-2xl hover:scale-105 transition-transform disabled:scale-100 disabled:opacity-60"
          >
            {isLoggingIn ? (
              <Loader2 className="w-6 h-6 animate-spin mr-2 inline" />
            ) : null}
            {isLoggingIn ? "Signing in…" : "Get Started Free"}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-300 text-sm">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4" fill="currentColor" />
            <span className="font-display text-white text-lg">Haven</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/child-safety" className="hover:text-white transition-colors">Child Safety</Link>
            <a href="mailto:support@havenpositive.online" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p>© {new Date().getFullYear()} Haven. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

const FeatureCard = ({ icon, title, body }) => (
  <div className="group p-7 rounded-2xl bg-white border border-gray-200 hover:shadow-lg hover:border-violet-200 transition-all duration-300">
    <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">{icon}</div>
    <h3 className="font-display text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 leading-relaxed text-sm">{body}</p>
  </div>
);